import styled from 'styled-components';

import {
  colors,
  letterSpacing,
  spacing,
  button,
  insetShadow,
  subtleShadow,
  borderRadius,
  smallShadow,
  duration,
  easing,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { CampaignAnalyticsCardHeader } from '../Styled';

export const PreviewContentBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  margin: 0;
  height: 100%;
`;

export const PreviewContentContainer = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: center;
  padding: ${spacing.plusHalf};
`;

export const PreviewContent = styled.div`
  display: flex;
  flex-direction: column;
  height: auto;
  letter-spacing: ${letterSpacing.pos10};
  min-height: ${pxToRem(600)};
  background-color: ${colors.backgroundTertiary};
  position: relative;
  width: 100%;
`;

export const HeatmapViz = styled.div`
  position: absolute !important;
  width: 100%;
  height: 100%;
  left: 0;
  overflow: hidden;
`;

export const TempHeatmapViz = styled.div`
  position: absolute !important;
  top: 0;
  width: 100%;
  height: 100%;
  left: 0;
  overflow: hidden;
`;

export const HeatmapCardHeader = styled(CampaignAnalyticsCardHeader)<{ $isFullscreen: boolean }>`
  position: sticky;
  top: 0;
  background: ${colors.white};
  z-index: 11;
  ${({ $isFullscreen }) => $isFullscreen && `z-index: 1002;`};
  padding: ${spacing.base} ${spacing.plusHalf};
  border-bottom: ${spacing.single} solid ${colors.borderPrimary};
`;

export const ContentWrapper = styled.div<{
  $isExpanded: boolean;
  $isFullscreen: boolean;
  $previewHeaderVisible: boolean;
  $heatmapHeaderHeight?: number;
  $previewBannerHeight?: number;
}>`
  overflow-y: hidden;
  max-height: ${({
    $isFullscreen,
    $previewHeaderVisible,
    $heatmapHeaderHeight = 75,
    $previewBannerHeight = 50,
  }) =>
    $isFullscreen
      ? `calc(100vh - ${
          $previewHeaderVisible
            ? pxToRem($previewBannerHeight + $heatmapHeaderHeight)
            : pxToRem($heatmapHeaderHeight)
        })`
      : pxToRem(680)};
  display: grid;
  grid-template-columns: ${({ $isExpanded }) => ($isExpanded ? ' 1fr 2fr;' : '0fr 1fr')};
  transition: grid-template-columns ${duration.long} ${easing.standard};
`;

export const OuterWrapper = styled.div<{ $isExpanded: boolean }>`
  width: ${({ $isExpanded }) => ($isExpanded ? '100%' : '0px')};
  background-color: ${({ theme }) => theme.colors.backgroundColors.backgroundPrimary};
`;

export const InnerWrapper = styled.div<{ $isExpanded: boolean }>`
  opacity: ${({ $isExpanded }) => ($isExpanded ? '1.0' : '0.0')};
  transition: all ${duration.long} ${easing.standard};
  height: ${({ $isExpanded }) => ($isExpanded ? '100%' : '0')};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-right: ${spacing.single} solid ${colors.borderPrimary};
`;

export const Details = styled.span`
  text-decoration: underline;
`;

export const LoadingContainer = styled.div<{ $isFullscreen: boolean }>`
  background: ${colors.backgroundScreen};
  display: flex;
  justify-content: center;
  align-items: center;
  position: sticky;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  height: ${({ $isFullscreen }) => ($isFullscreen ? '100vh' : pxToRem(680))};
`;

export const FullScreenAnalyticsCard = styled.div<{
  $isFullscreen: boolean;
  fullWidth?: boolean;
}>`
  background: ${colors.white};
  ${smallShadow};
  overflow: hidden;
  border-radius: ${spacing.half};
  margin-bottom: ${spacing.plusHalf};
  max-width: ${pxToRem(1128)};
  position: relative;
  z-index: 1;
  ${({ fullWidth }) =>
    fullWidth
      ? `
  flex: 1;
`
      : ''}
  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
    overflow: hidden;
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    max-width: 100%;
    height: 100%;
    z-index: 1001;
  `}
`;

export const DropdownButton = styled.button<{ $isFirst: boolean }>`
  ${button};
  align-items: center;
  background: ${colors.white};
  border: solid 1px ${colors.borderPrimary};
  color: ${colors.textPrimary};
  cursor: pointer;
  display: flex;
  gap: ${spacing.half};
  justify-content: center;
  outline: none;
  height: ${pxToRem(36)};
  & + & {
    border-left-color: transparent;
  }
  ${({ $isFirst }) =>
    $isFirst
      ? `padding-left: ${spacing.threeQuarter};
      padding-right: ${spacing.threeQuarter};
      border-top-left-radius: ${spacing.quarter};
      border-bottom-left-radius: ${spacing.quarter};
      border-right: none;`
      : `padding-left: ${spacing.half};
      padding-right: ${spacing.half};
      border-top-right-radius: ${spacing.quarter};
      border-bottom-right-radius: ${spacing.quarter};`};

  path {
    fill: ${colors.iconPrimary};
  }

  :hover {
    background: ${colors.purple10};
    border: ${spacing.single} solid ${colors.purple20};
    color: ${colors.purple90};
    ${({ $isFirst }) => $isFirst && `border-right: none;`}
    path {
      fill: ${colors.purple90};
    }
  }

  && {
    :focus {
      border-color: ${colors.purple50};
    }
  }

  :active {
    ${insetShadow};
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const ActionButtons = styled.div`
  display: flex;
  border-radius: ${borderRadius.md};
  box-shadow: ${subtleShadow.boxShadow};
  overflow: hidden;
  > *:nth-child(1):focus {
    border-right: 1px solid ${colors.purple50};
  }
  > *:nth-child(1):focus + div {
    > div > div > button {
      border-left: none;
    }
  }
  > *:nth-child(2) {
    > div > div {
      border: none;
      border-radius: inherit;
    }
  }
`;

export const ButtonBorder = styled.span`
  margin-left: ${spacing.half};
  margin-top: -${spacing.half};
  margin-bottom: -${spacing.half};
  border-left: 1px solid ${colors.borderPrimary};
`;

export const ScrollablePane = styled.div<{
  $previewHeaderVisible: boolean;
  $isFullscreen: boolean;
  $heatmapHeaderHeight?: number;
  $previewBannerHeight?: number;
}>`
  overflow-y: auto;
  max-height: ${({
    $isFullscreen,
    $previewHeaderVisible,
    $heatmapHeaderHeight = 75,
    $previewBannerHeight = 50,
  }) =>
    $isFullscreen
      ? `calc(100vh - ${
          $previewHeaderVisible
            ? pxToRem($previewBannerHeight + $heatmapHeaderHeight)
            : pxToRem($heatmapHeaderHeight)
        })`
      : pxToRem(680)};
`;
